import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Panel, PageHeader, Loading, StatusPill } from "@/components/portal/widgets";
import { Input } from "@/components/ui/input";
import { Download, Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { fmtDateTime, todayKey, dayKey } from "@/lib/time";

const PAGE = 50;
const dark = "rounded-md border border-slate-700 bg-slate-900 px-2 py-2 text-xs text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-blue-600";
const minus = (days) => { const d = new Date(`${todayKey()}T12:00:00`); d.setDate(d.getDate() - days); return d.toISOString().slice(0, 10); };
const PRESETS = [["Last 7 days", 7], ["30 days", 30], ["90 days", 90], ["1 year", 365]];
const STATUSES = ["Completed", "Cancelled", "No Show", "Missed", "Unassigned", "Assigned", "Will Call", "On Hold", "Driver En Route", "Arrived", "Passenger On Board", "In Progress"];
const CLOSED = /complete|cancel|no.?show|missed/i;
// Earlier-day trips: closed ones are filed in History, open ones wait in Day Review.
const placeTag = (t) => {
  if (t.day_review_scope) return t.day_review_scope === "history" ? "In history" : "In review";
  if (!t.scheduled_pickup || dayKey(t.scheduled_pickup) >= todayKey()) return "";
  return CLOSED.test(t.status || "") ? "In history" : "In review";
};

export default function TripHistoryPage() {
  const nav = useNavigate();
  const [start, setStart] = useState(minus(7));
  const [end, setEnd] = useState(todayKey());
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setSkip(0); }, [start, end, status, dq]);
  const qs = () => new URLSearchParams({ start, end, ...(status && { status }), ...(dq && { q: dq }) }).toString();

  useEffect(() => {
    let on = true;
    setData(null);
    fleetApi.get(`/trips/history?${qs()}&skip=${skip}&limit=${PAGE}`).then((r) => { if (on) setData(r.data); })
      .catch((e) => { if (on) { toast.error(fleetError(e, "Couldn't load trip history.")); setData({ total: 0, status_counts: {}, trips: [] }); } });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, status, dq, skip]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data: f } = await fleetApi.get(`/trips/history/export?${qs()}`);
      const bytes = Uint8Array.from(atob(f.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = f.filename || "trip-history.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(fleetError(e, "Export failed.")); } finally { setExporting(false); }
  };

  const counts = Object.entries(data?.status_counts || {}).sort((a, b) => b[1] - a[1]);
  const total = data?.total || 0;

  return (
    <div data-testid="trip-history-page">
      <PageHeader title="Trip History" subtitle="Every trip — on the board, in Day Review or filed in History.">
        <button onClick={exportCsv} disabled={exporting || !total} data-testid="history-export" className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-40">
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export CSV
        </button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input type="date" value={start} max={end} onChange={(e) => e.target.value && setStart(e.target.value)} className={dark} data-testid="history-start" aria-label="From" />
        <span className="text-xs text-slate-500">to</span>
        <input type="date" value={end} min={start} onChange={(e) => e.target.value && setEnd(e.target.value)} className={dark} data-testid="history-end" aria-label="To" />
        {PRESETS.map(([l, d]) => (
          <button key={d} onClick={() => { setStart(minus(d)); setEnd(todayKey()); }} data-testid={`history-preset-${d}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${start === minus(d) && end === todayKey() ? "border-blue-500 bg-blue-600/20 text-white" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>{l}</button>
        ))}
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={dark} data-testid="history-status">
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Trip #, rider or driver" className="w-60 border-slate-700 bg-slate-900 pl-8 text-white placeholder:text-slate-500" data-testid="history-search" />
        </div>
      </div>
      <p className="mb-3 text-xs text-slate-400" data-testid="history-counts">
        <span className="font-semibold text-white">{total.toLocaleString()} trips</span>{counts.length ? " · " : ""}{counts.map(([s, n]) => `${n} ${s}`).join(" · ")}
      </p>
      <Panel testid="history-table">
        {!data ? <Loading /> : (
          <div className="overflow-auto">
            <table className="w-full min-w-[880px] text-sm">
              <thead className="bg-[#111827]"><tr className="border-b border-slate-800 text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
                {["Trip #", "Date / time", "Status", "Rider", "Driver", "Pickup", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-800">
                {data.trips.map((t) => (
                  <tr key={t.id} onClick={() => nav(`/portal/trips/${t.id}`)} className="cursor-pointer hover:bg-slate-800/30" data-testid={`history-row-${t.id}`}>
                    <td className="px-4 py-2.5 font-mono-data text-xs text-slate-300">{t.external_trip_id}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-400">{fmtDateTime(t.scheduled_pickup)}</td>
                    <td className="px-4 py-2.5"><StatusPill value={t.status} /></td>
                    <td className="px-4 py-2.5 font-medium text-white">{t.rider_name}</td>
                    <td className="px-4 py-2.5 text-slate-300">{t.assigned_driver_name || <span className="text-slate-500">—</span>}</td>
                    <td className="max-w-xs truncate px-4 py-2.5 text-xs text-slate-400">{t.pickup_address}</td>
                    <td className="px-4 py-2.5">{placeTag(t) && (
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${placeTag(t) === "In history" ? "border-slate-600 text-slate-300" : "border-amber-500/40 text-amber-300"}`} data-testid={`history-tag-${t.id}`}>{placeTag(t)}</span>
                    )}</td>
                  </tr>
                ))}
                {!data.trips.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-500" data-testid="history-empty">No trips in this range.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {total > PAGE && (
          <div className="flex items-center justify-between border-t border-slate-800 px-5 py-3 text-xs text-slate-400" data-testid="history-pagination">
            <span>Showing {skip + 1}–{Math.min(skip + PAGE, total)} of {total}</span>
            <div className="flex gap-2">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="history-prev" className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-3 py-1.5 disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /> Prev</button>
              <button disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)} data-testid="history-next" className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-3 py-1.5 disabled:opacity-40">Next <ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
